/**
 * Small drawings for empty screens.
 *
 * Drawn out of the same few primitives as the first meeting's motifs — a line,
 * a circle, a bar — rather than illustrated, for the reason that file gives:
 * she is a rumour in a kitchen appliance, and anything that looked like a
 * mascot would answer a question the whole voice is built on leaving open.
 *
 * These are hairlines in her colour at low opacity, so they read as a mark
 * pressed into the page rather than as art placed on it. Never on a list that
 * is empty because a filter matched nothing — that emptiness is routine, and
 * decorating it is how a UI starts congratulating itself.
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.25,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className="size-12 text-[var(--her)] opacity-45"
      aria-hidden="true"
      role="presentation"
    >
      {children}
    </svg>
  )
}

/** An empty plate, seen from above. For a day with nothing written down. */
export function PlateMotif() {
  return (
    <Frame>
      <circle cx="24" cy="24" r="15" {...STROKE} />
      <circle cx="24" cy="24" r="9.5" {...STROKE} strokeDasharray="2 4" opacity="0.75" />
    </Frame>
  )
}

/** A week of bars with nothing in them. For trends with no data yet. */
export function WeekMotif() {
  return (
    <Frame>
      <path d="M8 34h32" {...STROKE} />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <path key={i} d={`M${10 + i * 4.7} 34v-${3 + (i % 3)}`} {...STROKE} opacity="0.7" />
      ))}
    </Frame>
  )
}

/** A shelf with one thing on it. For an empty catalogue or recipe list. */
export function ShelfMotif() {
  return (
    <Frame>
      <path d="M9 30h30" {...STROKE} />
      <rect x="20" y="20" width="8" height="10" rx="1.5" {...STROKE} />
      <path d="M9 38h30" {...STROKE} strokeDasharray="2 4" opacity="0.6" />
    </Frame>
  )
}

/** Her mark, reduced to a line and a dot. For a wait with nothing to show. */
export function WatchingMotif() {
  return (
    <Frame>
      <circle cx="24" cy="24" r="3.5" fill="currentColor" stroke="none" />
      <circle cx="24" cy="24" r="10" {...STROKE} opacity="0.6" />
      <circle cx="24" cy="24" r="16" {...STROKE} strokeDasharray="1.5 5" opacity="0.45" />
    </Frame>
  )
}
